/**
 * The thinking levels the composer's control offers: how much the model reasons (iq-ai-cio
 * `docs/features/thinking-levels.md`, "Clients"). Agentic, the turn's work budget, is a switch
 * of its own.
 *
 * Against an orchestrator whose `GET /api/v1/models` carries `thinking_scale`, the levels are
 * each model's own: its family's offered levels under the vendor's names, read from the shown
 * model's `thinking`, each with a rank on one shared ordinal scale. The stored choice is a
 * level's id and rank, so a model switch and another family's id land on the highest level the
 * shown family has at or below it: a switch never raises the level. Against an older orchestrator (no `thinking_scale`, or a failed read) the
 * control offers Low, Medium and High, sent as `fast`, `medium` and `high`, and `deep` while the
 * Agentic switch is on.
 */

/** A step of the shared scale (`thinking_scale`), and a family's level as `/models` lists it. */
export interface Level {
  id: string;
  label: string;
  rank: number;
}

/** A model's levels, as `/models` gives them on each entry (`thinking`). */
export interface ModelThinking {
  family: string;
  /** Its family's offered levels, in rank order. */
  levels: Level[];
  /** The client default's id: what the slider shows with nothing stored. */
  default: string;
  /** The server default's id: what a request that sends no level runs at. */
  server_default: string;
}

/** The stored choice: the chosen level's id and rank. */
export interface ThinkingChoice {
  id: string;
  rank: number;
}

/**
 * The scale's steps (`models.toml::thinking.scale`): the ranks an old stored name reads at.
 * Ranks are spaced by ten and never move, so a rank stored once keeps its meaning.
 */
export const SCALE: readonly Level[] = [
  { id: 'off', label: 'Off', rank: 0 },
  { id: 'minimal', label: 'Minimal', rank: 10 },
  { id: 'low', label: 'Low', rank: 20 },
  { id: 'medium', label: 'Medium', rank: 30 },
  { id: 'high', label: 'High', rank: 40 },
  { id: 'xhigh', label: 'Extra high', rank: 50 },
  { id: 'max', label: 'Max', rank: 60 },
];

/** Old names a store holds: `fast` names Low, `deep` Extra high (`models.toml::thinking.aliases`). */
const ALIASES: Record<string, string> = { fast: 'low', deep: 'xhigh' };

/** The scale's Low, Medium and High: an older orchestrator's three, and the levels with no model shown. */
const THREE = ['low', 'medium', 'high'];

const OLDER_LEVELS = SCALE.filter((s) => THREE.includes(s.id));

/**
 * An old stored name as a choice: `fast` at the Low rank, `deep` at the Extra high rank, a step
 * at its own (`off`, `low`, `medium`, `high`, `max`). Null for any other value.
 */
export function choiceOfName(name: string): ThinkingChoice | null {
  const id = ALIASES[name] ?? name;
  const step = SCALE.find((s) => s.id === id);
  return step ? { id: step.id, rank: step.rank } : null;
}

/** What the slider draws: its stops, the one it shows with nothing stored, and the server's kind. */
export interface LevelOffer {
  levels: Level[];
  defaultId: string;
  /** An older orchestrator: the stops are its three, sent by its names. */
  older: boolean;
}

/**
 * The shown model's stops. Against the current orchestrator, its family's offered levels; none
 * for a shown model whose `thinking` is null, which takes no level; with no model shown
 * (`thinking` undefined: a chat whose model this client cannot know) the scale's Low, Medium and
 * High, Low the default. Against an older orchestrator (`scale` undefined), its three.
 */
export function levelOffer(
  scale: Level[] | undefined,
  thinking: ModelThinking | null | undefined,
): LevelOffer {
  if (!scale) return { levels: OLDER_LEVELS, defaultId: 'low', older: true };
  if (thinking) return { levels: thinking.levels, defaultId: thinking.default, older: false };
  if (thinking === null) return { levels: [], defaultId: '', older: false };
  return { levels: scale.filter((s) => THREE.includes(s.id)), defaultId: 'low', older: false };
}

/** The highest offered level at or below `rank`; the lowest when none is (a forced raise). */
export function atOrBelow(levels: Level[], rank: number): Level | undefined {
  let best: Level | undefined;
  for (const level of levels)
    if (level.rank <= rank && (!best || level.rank > best.rank)) best = level;
  return best ?? levels[0];
}

/**
 * The level the slider shows: the one with the stored id (any case), else the highest offered
 * at or below the stored rank (the lowest when none is), so a model switch never raises it; with
 * nothing stored, the shown model's default. Null for a model with no levels. The request sends
 * the shown level's id (levelToSend), so the server's own nearest rule never applies.
 */
export function shownLevel(offer: LevelOffer, choice: ThinkingChoice | null): Level | null {
  const { levels, defaultId } = offer;
  if (choice) {
    const id = choice.id.toLowerCase();
    return levels.find((l) => l.id.toLowerCase() === id) ?? atOrBelow(levels, choice.rank) ?? null;
  }
  return levels.find((l) => l.id === defaultId) ?? levels[0] ?? null;
}

/**
 * The scale's step for a stored choice: the one with its id (any case), else the highest at or
 * below its rank. What the Thinking chip names while the chat's model is unavailable, whose own
 * levels the list no longer gives. Null without a scale or a choice.
 */
export function scaleLevelOf(
  scale: Level[] | undefined,
  choice: ThinkingChoice | null,
): Level | null {
  if (!scale || !choice) return null;
  const id = choice.id.toLowerCase();
  return scale.find((s) => s.id.toLowerCase() === id) ?? atOrBelow(scale, choice.rank) ?? null;
}

/** The highest offered level: what "Up to" names. Null for a model with no levels. */
export function capOf(offer: LevelOffer): Level | null {
  return offer.levels[offer.levels.length - 1] ?? null;
}

/**
 * The level a pick of a model whose levels are `target` lands on, from the level shown now
 * (`current`), else the stored choice: the target's level with that id, else its highest at or
 * below that rank. Null for a model with no levels.
 */
export function levelAfterPick(
  scale: Level[] | undefined,
  target: ModelThinking | null,
  current: Level | null,
  choice: ThinkingChoice | null,
): Level | null {
  return shownLevel(
    levelOffer(scale, target),
    current ? { id: current.id, rank: current.rank } : choice,
  );
}

/** Bars of the five-bar glyph lit for a rank: Low 1, Medium 2, High 3, Extra high 4, Max 5, Off 0. */
export function barsOf(rank: number): number {
  return Math.max(0, Math.min(5, Math.round((rank - 10) / 10)));
}

/** A stop of the Thinking dial: a level the model offers, or a scale step above its cap. */
export interface Stop {
  level: Level;
  offered: boolean;
}

/**
 * The dial's stops: the offered levels, then the scale's steps above the cap, drawn hatched. An
 * older server, or a model with no levels: its offered levels alone.
 */
export function drawnStops(scale: Level[] | undefined, offer: LevelOffer): Stop[] {
  const offered = offer.levels.map((level) => ({ level, offered: true }));
  const cap = capOf(offer);
  if (!scale || !cap) return offered;
  return [
    ...offered,
    ...scale.filter((s) => s.rank > cap.rank).map((level) => ({ level, offered: false })),
  ];
}

/** An older orchestrator's name for a rank and the switch: `deep` while it is on, else its three. */
export function olderName(rank: number, agentic: boolean): string {
  if (agentic) return 'deep';
  if (rank <= 20) return 'fast';
  return rank < 40 ? 'medium' : 'high';
}

/**
 * The request's `thinking_level`: the shown level's id, or an older orchestrator's name for it.
 * Null for a model with no levels, whose requests carry none.
 */
export function levelToSend(
  offer: LevelOffer,
  choice: ThinkingChoice | null,
  agentic: boolean,
): string | null {
  const level = shownLevel(offer, choice);
  if (level === null) return null;
  return offer.older ? olderName(level.rank, agentic) : level.id;
}

/** A catalog key (`public/locales/<lng>/ai-cio.json`) and its English text. */
export type CatalogEntry = readonly [key: string, english: string];

/**
 * The names the catalog holds: the scale's steps, `none` (named Off, as `off` is) and Claude's
 * Off-rank thinking types. Any other id (a budget level, a new family's level) shows the
 * server's label.
 */
const NAMES: Record<string, CatalogEntry> = {
  off: ['level.off', 'Off'],
  none: ['level.off', 'Off'],
  minimal: ['level.minimal', 'Minimal'],
  low: ['level.low', 'Low'],
  medium: ['level.medium', 'Medium'],
  high: ['level.high', 'High'],
  xhigh: ['level.xhigh', 'Extra high'],
  max: ['level.max', 'Max'],
  disabled: ['level.disabled', 'Off'],
  between_tools: ['level.betweenTools', 'Between tools'],
};

/** The catalog entry of a level's name, by its id lowercased, or null when the label shows. */
export function nameEntry(id: string): CatalogEntry | null {
  return NAMES[id.toLowerCase()] ?? null;
}

/** The hints by rank: what each step of the scale does. */
const HINTS: Record<number, CatalogEntry> = {
  0: ['level.hint.off', 'No reasoning before answering'],
  20: ['level.hint.low', 'Least reasoning, quickest answer'],
  30: ['level.hint.medium', 'Some reasoning before answering'],
  40: ['level.hint.high', 'Reasons more before answering'],
  50: ['level.hint.xhigh', 'Reasons longer, for hard multi-step work; slower'],
  60: ['level.hint.max', 'The most reasoning; slowest'],
};

const BETWEEN_TOOLS_HINT: CatalogEntry = [
  'level.hint.betweenTools',
  'No reasoning before answering; short notes between tool calls',
];

/** An older orchestrator's hints, by its three. */
const OLDER_HINTS: Record<string, CatalogEntry> = {
  low: ['level.olderHint.low', 'The default: quick answer, fewer steps'],
  medium: ['level.olderHint.medium', 'Standard depth'],
  high: ['level.olderHint.high', 'More steps and checks'],
};

/** The catalog entry of a level's hint, by its rank (Between tools its own), or null for none. */
export function hintEntry(level: Level, offer: LevelOffer): CatalogEntry | null {
  if (offer.older) return OLDER_HINTS[level.id] ?? null;
  if (level.id.toLowerCase() === 'between_tools') return BETWEEN_TOOLS_HINT;
  return HINTS[level.rank] ?? null;
}
